// game/generators.ts — destructible spawners.
// Generators are the strategic question of every room: thin the horde, or cut
// the source (DESIGN §4, §5 Zone 1). Rate and cap are modulated by the Gloom.

import type { GeneratorDef, Vec } from '../types'
import { TUNING } from '../data/tuning'
import { drawSprite } from '../engine/sprites'
import { emit } from '../engine/particles'
import { fx } from '../engine/fx'
import type { Run } from './game'

export class GeneratorInst {
  def!: GeneratorDef
  pos: Vec = { x: 0, y: 0 }
  hp = 1
  maxHp = 1
  alive = false
  /** children currently alive from this generator */
  live = 0
  timer = 0
  radius = 16
  index = 0
  hitFlashT = 0
  bob = 0
  lungeMarked = false
  spriteId = ''
}

const spawnPos: Vec = { x: 0, y: 0 }

export function makeGenerator(def: GeneratorDef, x: number, y: number, index: number, hpScale: number): GeneratorInst {
  const g = new GeneratorInst()
  g.def = def
  g.pos.x = x
  g.pos.y = y
  g.maxHp = Math.max(1, Math.round(def.hp * hpScale))
  g.hp = g.maxHp
  g.alive = true
  g.radius = def.radius
  g.index = index
  g.spriteId = def.id
  // stagger the first pour so a three-generator room doesn't dump at t=0
  g.timer = def.interval * (0.35 + 0.3 * index)
  return g
}

export function updateGenerator(run: Run, g: GeneratorInst, dt: number): void {
  if (!g.alive) return
  if (g.hitFlashT > 0) g.hitFlashT -= dt
  g.bob += dt

  const rateMul = run.gloomSys.generatorRateMul()
  const cap = Math.floor(g.def.cap * run.gloomSys.capMul())
  g.timer -= dt * rateMul
  if (g.timer > 0) return

  g.timer = g.def.interval
  if (g.live >= cap) return

  const burst = Math.max(1, g.def.burst)
  let spawned = 0
  for (let i = 0; i < burst && g.live < cap; i++) {
    if (!run.world.findOpenNear(g.pos.x, g.pos.y, g.radius + 14, g.radius + 60, run.rngObj, spawnPos, 10)) continue
    const e = run.spawnEnemy(g.def.spawns, spawnPos)
    if (!e.alive) continue
    e.genIndex = g.index
    g.live++
    spawned++
    emit(spawnPos.x, spawnPos.y, {
      count: 8,
      color: '#6b5a8a',
      speed: 90,
      life: 0.34,
      size: 3,
      shape: 1,
    })
  }
  if (spawned > 0) {
    emit(g.pos.x, g.pos.y - 4, {
      count: 6,
      color: '#8a7ab0',
      speed: 60,
      life: 0.4,
      size: 3,
    })
  }
}

export function killGenerator(run: Run, g: GeneratorInst): void {
  if (!g.alive) return
  g.alive = false
  g.hp = 0
  run.sfx('generator-die')
  fx.shake(TUNING.fx.shakeExplosion, 0.32)
  fx.hitstop(TUNING.fx.hitstopKill * 1.6)
  emit(g.pos.x, g.pos.y, {
    count: 26,
    color: '#d8c9a0',
    speed: 190,
    speedVar: 140,
    life: 0.6,
    lifeVar: 0.3,
    size: 4,
    grav: 260,
    drag: 0.86,
  })
  emit(g.pos.x, g.pos.y, {
    count: 12,
    color: '#ff9a3c',
    speed: 120,
    life: 0.45,
    size: 4,
    shape: 1,
  })
  run.addEmbers(g.def.emberValue)
  run.featTick('generatorsDown')
  run.bark('A source stops.')
}

export function renderGenerator(ctx: CanvasRenderingContext2D, g: GeneratorInst, gloomRate: number): void {
  if (!g.alive) return

  ctx.globalAlpha = 0.35
  ctx.fillStyle = '#000000'
  ctx.beginPath()
  ctx.ellipse(g.pos.x, g.pos.y + g.radius * 0.72, g.radius * 0.95, g.radius * 0.36, 0, 0, Math.PI * 2)
  ctx.fill()

  // gloom rune on the floor: a generator must never be mistaken for a body
  const pulse = 0.5 + 0.5 * Math.sin(g.bob * 2.2)
  ctx.globalAlpha = 0.16 + 0.12 * pulse
  ctx.fillStyle = '#8a4fd8'
  ctx.beginPath()
  ctx.ellipse(g.pos.x, g.pos.y + g.radius * 0.6, g.radius * 1.5, g.radius * 0.62, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 0.3 + 0.2 * pulse
  ctx.strokeStyle = '#b078ff'
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.ellipse(g.pos.x, g.pos.y + g.radius * 0.6, g.radius * 1.5, g.radius * 0.62, 0, 0, Math.PI * 2)
  ctx.stroke()
  ctx.globalAlpha = 1

  // charge-up glow: the room can read when the next pour lands
  const t = Math.max(0, Math.min(1, 1 - g.timer / Math.max(0.001, g.def.interval / gloomRate)))
  if (t > 0.6) {
    const k = (t - 0.6) / 0.4
    ctx.globalAlpha = 0.25 * k
    ctx.fillStyle = '#9a7ad8'
    ctx.beginPath()
    ctx.arc(g.pos.x, g.pos.y, g.radius + 8 + k * 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
  }

  drawSprite(ctx, g.spriteId, g.pos.x, g.pos.y - 2, {
    white: g.hitFlashT > 0 ? 1 : 0,
  })

  const w = g.radius * 2.4
  const x = g.pos.x - w / 2
  const y = g.pos.y - g.radius - 14
  ctx.fillStyle = '#00000099'
  ctx.fillRect(x - 1, y - 1, w + 2, 5)
  ctx.fillStyle = '#c9a227'
  ctx.fillRect(x, y, w * Math.max(0, g.hp / g.maxHp), 3)
}
